import {
  Literal,
  Null,
  Record,
  Static,
  String,
  Undefined,
  Union,
} from "runtypes";
import { z } from "zod";

import { getNormalEnum } from "./runtypeEnums.js";

export const ScopeItemTypeLiteral = Union(
  Literal("MODULE"),
  Literal("FUNCTION"),
  Literal("CLASS"),
  Literal("ARRAY"),
  Literal("DATAFRAME"),
  Literal("REMOTE_DATAFRAME"),
  Literal("BIGFRAMES_DATAFRAME"),
  Literal("SNOWPARK_DATAFRAME"),
  Literal("PIVOT_TABLE"),
  Literal("STRING"),
  Literal("NUMBER"),
  Literal("BOOLEAN"),
  Literal("DICTIONARY"),
  Literal("DATE"),
  Literal("DATETIME"),
  Literal("UNKNOWN"),
  Literal("DB_TABLE"),
  Literal("BYTESTREAM"),
  Literal("BYTES"),
  // This is deprecated, but kept around for backwards compatability. Scope
  // items that are unable to be parsed will now have type UNKNOWN
  Literal("ERROR"),
  Literal("TBD"),
  // SUP-571: we write this tombstone value instead of deleting to avoid multiplayer versioning issues
  Literal("DELETED"),
);

export type ScopeItemType = Static<typeof ScopeItemTypeLiteral>;
export const ScopeItemType = getNormalEnum(ScopeItemTypeLiteral);

const HUMAN_STRING_BY_SCOPE_TYPE: { [key in ScopeItemType]: string } = {
  [ScopeItemType.MODULE]: "module",
  [ScopeItemType.FUNCTION]: "function",
  [ScopeItemType.CLASS]: "class",
  [ScopeItemType.ARRAY]: "list",
  [ScopeItemType.DATAFRAME]: "dataframe",
  [ScopeItemType.REMOTE_DATAFRAME]: "query",
  [ScopeItemType.BIGFRAMES_DATAFRAME]: "BigQuery dataframe",
  [ScopeItemType.SNOWPARK_DATAFRAME]: "Snowpark dataframe",
  [ScopeItemType.PIVOT_TABLE]: "Pivot table",
  [ScopeItemType.STRING]: "string",
  [ScopeItemType.NUMBER]: "number",
  [ScopeItemType.BOOLEAN]: "boolean",
  [ScopeItemType.DICTIONARY]: "dict",
  [ScopeItemType.DATE]: "date",
  [ScopeItemType.DATETIME]: "datetime",
  [ScopeItemType.UNKNOWN]: "unknown",
  [ScopeItemType.DB_TABLE]: "database table",
  [ScopeItemType.ERROR]: "error",
  [ScopeItemType.TBD]: "tbd",
  [ScopeItemType.DELETED]: "deleted",
  [ScopeItemType.BYTESTREAM]: "bytestream",
  [ScopeItemType.BYTES]: "bytes",
};

export const scopeTypeToHumanString = (type: ScopeItemType): string =>
  HUMAN_STRING_BY_SCOPE_TYPE[type] ??
  HUMAN_STRING_BY_SCOPE_TYPE[ScopeItemType.UNKNOWN];

// !!! IMPORTANT !!! This needs to be kept in sync with @hex/python-kernel-startup/python_kernel_startup/hex_scope.py

// Every schema in this tree is `.passthrough()`: the producer is `hex_scope.py`,
// which ships in the kernel image customers pin, so it can emit keys a given
// server build has never heard of. Dropping them would lose kernel data on the
// way to the client mid-deploy, and `stripToSchema` would drop them a second
// time on the way out. This applies at every level, not just the top -- a bare
// nested `z.object` reintroduces the bug for its own keys.
const dataFrameSchemaFields = {
  columns: z.record(z.string(), z.string()),
  columnsCount: z.number().optional(),
  size: z.number().optional(),
  rowCount: z.number().optional(),
  rowCountIsTruncated: z.boolean().optional(),
};

/**
 * The keys we know about. Deliberately *not* `z.infer` of the passthrough
 * schema below: that yields a `[k: string]: unknown` index signature, which
 * `QueryDeepPartialEntity` cannot map (it rejects `unknown`) and which no
 * caller can act on anyway -- we cannot name keys we have never heard of. The
 * unknown keys still ride along at runtime, which is the point; this is the
 * jsonb column's type and the GraphQL scalar's type, so keeping it strict
 * keeps those boundaries usable.
 */
export type DataFrameSchema = z.infer<
  z.ZodObject<typeof dataFrameSchemaFields>
>;

export const DataFrameSchema: z.ZodType<
  DataFrameSchema,
  z.ZodTypeDef,
  unknown
> = z.object(dataFrameSchemaFields).passthrough();

export const ScopeItem = z
  .object({
    name: z.string(),
    displayValue: z.string(),
    // `.catch` rather than a bare enum: the producer is `hex_scope.py`, which ships in
    // the kernel image customers pin, so this schema and the values reaching it are on
    // independent release cycles. A member this build has not heard of would otherwise
    // fail the whole `addAppSessionEvents` batch that carries the `UPSERT_SCOPE_ITEM`
    // event. `UNKNOWN` is the value the rest of the codebase already falls back to --
    // see `scopeTypeToHumanString` -- and the deprecated `ERROR`/`TBD` members above
    // are what this list looks like when it goes the other way.
    //
    // Note this is not what `.passthrough()` below does: that preserves *keys* the
    // shape does not declare, and has no bearing on the value of a declared key.
    type: z.nativeEnum(ScopeItemType).catch(ScopeItemType.UNKNOWN),
    rawType: z.string(),
    isHidden: z.boolean(),
    dataFrameSchema: DataFrameSchema.nullable(),
  })
  .passthrough();
export type ScopeItem = z.infer<typeof ScopeItem>;

export const LegacyScope = z.array(ScopeItem);
export type LegacyScope = z.infer<typeof LegacyScope>;

export const ScopeMetadata = z
  .object({
    executionDurationInSeconds: z.number().optional(),
  })
  .passthrough();
export type ScopeMetadata = z.infer<typeof ScopeMetadata>;

export const ScopeWithMetadata = z
  .object({
    metadata: ScopeMetadata.optional(),
    scopeItems: z.array(ScopeItem),
  })
  .passthrough();
export type ScopeWithMetadata = z.infer<typeof ScopeWithMetadata>;

export const MemoryStats = Record({
  total: String,
  available: String,
  max: String,
});
export type MemoryStats = Static<typeof MemoryStats>;

export const ScopeError = Record({
  message: Union(String, Null, Undefined),
  traceback: Union(String, Null, Undefined),
});
export type ScopeError = Static<typeof ScopeError>;
